import { z } from 'zod';

import {
  TASK_COLORS,
  TASK_STAGES,
  TASK_URGENCIES,
  type TaskColor,
  type TaskStage,
  type TaskUrgency,
} from '../domain.js';
import { dateOnlySchema, idSchema, optionalText } from './common.js';

/**
 * Onde a tarefa está.
 *
 * Ou uma etapa do quadro Pessoal, ou uma coluna de um quadro próprio — nunca
 * as duas. O `superRefine` é o que sustenta essa invariante na entrada; o
 * serviço a repete na escrita, porque contrato não protege o banco sozinho.
 */
const placementFields = {
  stage: z.enum(TASK_STAGES).optional(),
  columnId: idSchema.optional(),
};

function placementIsExclusive(
  value: { stage?: TaskStage | undefined; columnId?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (value.stage && value.columnId) {
    ctx.addIssue({
      code: 'custom',
      message: 'Informe a etapa ou a coluna, não as duas',
      path: ['columnId'],
    });
  }
}

// ─────────────────────────────────────────────────────────── quadros

export const createBoardBodySchema = z.object({
  name: z.string().trim().min(2, 'Dê um nome ao quadro').max(80),
  description: optionalText(300),
  color: z.enum(TASK_COLORS).optional(),
  /**
   * Colunas iniciais. Ausente, o quadro nasce com as três de sempre — é o que
   * a maioria quer, e uma coluna a mais é um clique.
   */
  columns: z.array(z.string().trim().min(1).max(40)).min(1).max(12).optional(),
  /** Quem já entra com assento, além de quem cria. */
  memberIds: z.array(idSchema).max(50).optional(),
});
export type CreateBoardBody = z.infer<typeof createBoardBodySchema>;

export const updateBoardBodySchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: optionalText(300),
  color: z.enum(TASK_COLORS).nullable().optional(),
});
export type UpdateBoardBody = z.infer<typeof updateBoardBodySchema>;

export const boardMemberBodySchema = z.object({ userId: idSchema });
export type BoardMemberBody = z.infer<typeof boardMemberBodySchema>;

export const createColumnBodySchema = z.object({
  name: z.string().trim().min(1, 'Dê um nome à coluna').max(40),
});
export type CreateColumnBody = z.infer<typeof createColumnBodySchema>;

export const updateColumnBodySchema = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  position: z.number().int().min(0).max(50).optional(),
});
export type UpdateColumnBody = z.infer<typeof updateColumnBodySchema>;

// ─────────────────────────────────────────────────────────── tarefas

/**
 * Checklist como texto corrido: uma linha, um item.
 *
 * Chega inteiro a cada gravação e substitui a lista. O serviço reaproveita os
 * itens de mesmo conteúdo para não perder o que já estava marcado — quem
 * acrescenta uma linha no fim não desmarca o resto.
 */
const checklistTextSchema = z.string().max(4000);

export const createTaskBodySchema = z
  .object({
    ...placementFields,
    boardId: idSchema.optional(),
    title: z.string().trim().min(2, 'Dê um título à tarefa').max(200),
    description: optionalText(4000),
    dueAt: dateOnlySchema.optional(),
    color: z.enum(TASK_COLORS).optional(),
    urgency: z.enum(TASK_URGENCIES).optional(),
    participantIds: z.array(idSchema).max(30).optional(),
    checklist: checklistTextSchema.optional(),
  })
  .superRefine((value, ctx) => {
    placementIsExclusive(value, ctx);

    if (value.boardId && !value.columnId) {
      ctx.addIssue({ code: 'custom', message: 'Escolha a coluna', path: ['columnId'] });
    }
    if (!value.boardId && !value.stage) {
      ctx.addIssue({ code: 'custom', message: 'Escolha a etapa', path: ['stage'] });
    }
  });
export type CreateTaskBody = z.infer<typeof createTaskBodySchema>;

/**
 * Campos que só o autor e o gestor alteram.
 *
 * Mover é outra coisa: qualquer participante arrasta o cartão, senão o quadro
 * compartilhado perderia a graça. A separação vale no serviço, que rejeita
 * conteúdo vindo de quem não pode.
 */
export const TASK_CONTENT_FIELDS = [
  'title',
  'description',
  'dueAt',
  'color',
  'urgency',
  'checklist',
] as const;

export const updateTaskBodySchema = z
  .object({
    ...placementFields,
    title: z.string().trim().min(2).max(200).optional(),
    description: optionalText(4000),
    dueAt: dateOnlySchema.optional(),
    color: z.enum(TASK_COLORS).nullable().optional(),
    urgency: z.enum(TASK_URGENCIES).optional(),
    /** Índice dentro da coluna de destino. Ausente, vai para o fim. */
    position: z.number().int().min(0).max(9999).optional(),
    checklist: checklistTextSchema.optional(),
  })
  .superRefine(placementIsExclusive);
export type UpdateTaskBody = z.infer<typeof updateTaskBodySchema>;

export const addParticipantBodySchema = z.object({ userId: idSchema });
export type AddParticipantBody = z.infer<typeof addParticipantBodySchema>;

export const toggleChecklistItemBodySchema = z.object({ isDone: z.boolean() });
export type ToggleChecklistItemBody = z.infer<typeof toggleChecklistItemBodySchema>;

/** Query da listagem: qual quadro abrir. Ausente, o Pessoal. */
export const listTasksQuerySchema = z.object({ boardId: idSchema.optional() });
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

// ─────────────────────────────────────────────────────── respostas

export interface TaskPerson {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
}

export interface TaskChecklistItem {
  readonly id: string;
  readonly content: string;
  readonly isDone: boolean;
  readonly position: number;
  /** Quem marcou. É o registro de autoria do que foi feito. */
  readonly doneBy: TaskPerson | null;
  readonly doneAt: string | null;
  /** Quem olha pode marcar ou desmarcar este item — ver `canToggleChecklistItem`. */
  readonly canToggle: boolean;
}

export interface TaskSummary {
  readonly id: string;
  readonly boardId: string | null;
  readonly columnId: string | null;
  readonly stage: TaskStage | null;
  readonly title: string;
  readonly description: string | null;
  readonly dueAt: string | null;
  readonly color: TaskColor | null;
  readonly urgency: TaskUrgency;
  readonly position: number;
  readonly createdBy: TaskPerson;
  readonly participants: readonly TaskPerson[];
  readonly checklist: readonly TaskChecklistItem[];
  readonly completedAt: string | null;
  readonly createdAt: string;
  /** Quem olha pode editar o conteúdo, excluir e mexer nos participantes. */
  readonly canManage: boolean;
}

export interface BoardColumn {
  readonly id: string;
  readonly name: string;
  readonly position: number;
}

/**
 * Quadro na lista lateral.
 *
 * O Pessoal vem com `id: 'pessoal'` e `isPersonal: true` — não existe linha
 * correspondente no banco, é a visão das tarefas sem quadro em que a pessoa
 * participa.
 */
export interface BoardSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly color: TaskColor | null;
  readonly isPersonal: boolean;
  readonly columns: readonly BoardColumn[];
  readonly members: readonly TaskPerson[];
  /** Tarefas vivas. Não conta as excluídas. */
  readonly taskCount: number;
  /**
   * Nunca teve tarefa — nem viva, nem excluída.
   *
   * É o que decide entre apagar de vez e arquivar: sem tarefa não há histórico
   * a preservar, e um quadro vazio no arquivo é só entulho.
   */
  readonly isEmpty: boolean;
  /** Quem olha criou o quadro, então pode renomear, arquivar e mexer nos membros. */
  readonly canManage: boolean;
}

export interface BoardWithTasks {
  readonly board: BoardSummary;
  readonly tasks: readonly TaskSummary[];
}
